import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { pdfExportService } from '../../../services/pdfExportService';
import { auditService } from '../../../services/auditService';
import { useLanguage } from '../../../contexts/LanguageContext';

const AuditCard = ({ audit, onView, siteName, companyName, onDelete, onRestore }) => {
  const [exporting, setExporting] = useState(false);
  const { t } = useLanguage();

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success bg-success/10';
      case 'in_progress':
        return 'text-warning bg-warning/10';
      case 'draft':
        return 'text-muted-foreground bg-muted';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return t('completed');
      case 'in_progress':
        return t('inProgress');
      case 'draft':
        return t('draft');
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const [questionsResult, responsesResult] = await Promise.all([
        auditService?.getAuditQuestions(audit?.id),
        auditService?.getAuditResponses(audit?.id),
      ]);
      await pdfExportService?.generateAuditReport({
        audit,
        questions: questionsResult?.data || [],
        responses: responsesResult?.data || [],
        siteName: siteName || audit?.sites?.site_name || '',
        companyName: companyName || '',
        auditorName: audit?.auditor_name || '',
        completedAt: audit?.audit_date,
      });
    } catch (err) {
      console.error('Audit PDF export error:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 shadow-elevation-2 hover:shadow-elevation-3 transition-smooth">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-foreground mb-1">{audit?.audit_name}</h3>
          <p className="text-sm text-muted-foreground">{audit?.sites?.site_name || t('unknownSite')}</p>
        </div>
        <span className={`px-2 py-1 rounded-md text-xs font-medium ${getStatusColor(audit?.status)}`}>
          {getStatusLabel(audit?.status)}
        </span>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
          <span className="text-muted-foreground">{t('date')}:</span>
          <span className="text-foreground font-medium">{formatDate(audit?.audit_date)}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Icon name="User" size={16} color="var(--color-muted-foreground)" />
          <span className="text-muted-foreground">{t('auditor')}:</span>
          <span className="text-foreground font-medium">{audit?.auditor_name}</span>
        </div>
        {audit?.overall_result && (
          <div className="flex items-center gap-2 text-sm">
            <Icon name="Award" size={16} color="var(--color-muted-foreground)" />
            <span className="text-muted-foreground">{t('result')}:</span>
            <span className="text-foreground font-medium">{audit?.overall_result}</span>
          </div>
        )}
      </div>

      <Button
        variant="outline"
        size="sm"
        fullWidth
        iconName="Eye"
        onClick={onView}
      >
        {t('viewDetails')}
      </Button>
      {audit?.status === 'completed' && (
        <Button
          variant="outline"
          size="sm"
          fullWidth
          iconName={exporting ? 'Loader' : 'Download'}
          onClick={handleExportPDF}
          disabled={exporting}
          className="mt-2"
        >
          {exporting ? t('exporting') : t('exportPDF')}
        </Button>
      )}
      {/* NSherjFh PR 7: admin Delete (active view) or Restore (deleted
          view), mutually exclusive at the page level. */}
      {onDelete && (
        <Button
          variant="outline"
          size="sm"
          fullWidth
          iconName="Trash2"
          onClick={() => onDelete(audit)}
          className="mt-2 text-destructive hover:text-destructive/80 border-destructive/30 hover:bg-destructive/5"
          aria-label="Delete audit"
        >
          Delete
        </Button>
      )}
      {onRestore && (
        <Button
          variant="outline"
          size="sm"
          fullWidth
          iconName="RotateCcw"
          onClick={() => onRestore(audit)}
          className="mt-2 text-accent hover:text-accent/80 border-accent/30 hover:bg-accent/5"
          aria-label="Restore audit"
        >
          Restore
        </Button>
      )}
    </div>
  );
};

export default AuditCard;